import {
	ReactNode,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { hideModal } from "../../ui/gsap-animations/hide-modal";
import { showModal } from "../../ui/gsap-animations/show-modal";
import "./modal.scss";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "docs";

type ModalProps = {
	isOpen: boolean;
	onClose: () => void;
	title?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
	closeOnOverlayClick?: boolean;
	closeOnEsc?: boolean;
	size?: ModalSize;
};

const getFocusableElements = (root: HTMLElement) => {
	const selector =
		'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
	return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
		(el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
	);
};

export const Modal = ({
	isOpen,
	onClose,
	title,
	children,
	footer,
	closeOnOverlayClick = true,
	closeOnEsc = true,
	size = "md",
}: ModalProps) => {
	const titleId = useId();
	const overlayRef = useRef<HTMLDivElement | null>(null);
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const tlRef = useRef<ReturnType<typeof showModal> | null>(null);
	// Окно остаётся в DOM, пока проигрывается анимация закрытия,
	// поэтому монтирование отвязано от пропа isOpen.
	const [isMounted, setIsMounted] = useState(isOpen);

	// Любой запрос на закрытие сводится к onClose() — родитель переключает
	// isOpen в false, а сама анимация выхода проигрывается ниже в эффекте.
	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	// Открытие: монтируем окно, как только isOpen стал true.
	useEffect(() => {
		if (isOpen) setIsMounted(true);
	}, [isOpen]);

	// Анимация появления (после монтирования).
	useLayoutEffect(() => {
		if (!isOpen || !isMounted) return;
		const overlay = overlayRef.current;
		const dialog = dialogRef.current;
		if (!overlay || !dialog) return;

		tlRef.current?.kill();
		tlRef.current = showModal(overlay, dialog);
		return () => {
			tlRef.current?.kill();
			tlRef.current = null;
		};
	}, [isOpen, isMounted]);

	// Анимация закрытия: isOpen стал false, но окно ещё в DOM.
	useLayoutEffect(() => {
		if (isOpen || !isMounted) return;
		const overlay = overlayRef.current;
		const dialog = dialogRef.current;
		if (!overlay || !dialog) {
			setIsMounted(false);
			return;
		}
		tlRef.current?.kill();
		tlRef.current = hideModal(overlay, dialog, {
			onComplete: () => setIsMounted(false),
		});
	}, [isOpen, isMounted]);

	const ariaLabelledBy = useMemo(() => {
		if (!title) return undefined;
		return titleId;
	}, [title, titleId]);

	useEffect(() => {
		if (!isMounted) return;

		const prevActive = document.activeElement as HTMLElement | null;
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const focusInitial = () => {
			const dialog = dialogRef.current;
			if (!dialog) return;
			const focusables = getFocusableElements(dialog);
			(focusables[0] ?? dialog).focus();
		};

		// Wait for portal content to paint.
		const raf = window.requestAnimationFrame(focusInitial);

		const onKeyDown = (e: KeyboardEvent) => {
			if (!dialogRef.current) return;

			if (e.key === "Escape" && closeOnEsc) {
				e.preventDefault();
				handleClose();
				return;
			}

			if (e.key === "Tab") {
				const focusables = getFocusableElements(dialogRef.current);
				if (focusables.length === 0) {
					e.preventDefault();
					dialogRef.current.focus();
					return;
				}

				const first = focusables[0];
				const last = focusables[focusables.length - 1];
				const active = document.activeElement as HTMLElement | null;

				if (e.shiftKey) {
					if (
						!active ||
						active === first ||
						!dialogRef.current.contains(active)
					) {
						e.preventDefault();
						last.focus();
					}
				} else {
					if (active === last) {
						e.preventDefault();
						first.focus();
					}
				}
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => {
			window.cancelAnimationFrame(raf);
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = prevOverflow;
			// preventScroll: иначе возврат фокуса прокручивает overflow-контейнеры
			// (например .chat-item) по горизонтали и контент уезжает в сторону.
			prevActive?.focus?.({ preventScroll: true });
		};
	}, [isMounted, handleClose, closeOnEsc]);

	if (!isMounted) return null;

	return createPortal(
		<div className='modal' aria-hidden={false}>
			<div
				ref={overlayRef}
				className='modal__overlay'
				onMouseDown={() => {
					if (!closeOnOverlayClick) return;
					handleClose();
				}}
			/>
			<div
				ref={dialogRef}
				className={`modal__dialog modal__dialog--${size}`}
				role='dialog'
				aria-modal='true'
				aria-labelledby={ariaLabelledBy}
				tabIndex={-1}>
				{title && (
					<div className='modal__header'>
						<div className='modal__title' id={titleId}>
							{title}
						</div>
						<button
							className='modal__close'
							type='button'
							onClick={handleClose}>
							<img src='/img/icons/close.png' alt='close' />
						</button>
					</div>
				)}
				<div className='modal__body'>{children}</div>
				{footer && <div className='modal__footer'>{footer}</div>}
			</div>
		</div>,
		document.body,
	);
};
